import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { name: "首页", path: "/" },
  { name: "赛事分类", path: "/category" },
  { name: "赛事专题", path: "/special" },
  { name: "关于我们", path: "/about" },
];

export default function SiteNavbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const query = keyword.trim();
    if (!query) return;
    navigate(`/category?q=${encodeURIComponent(query)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="hidden bg-primary-950 text-background-100 md:block">
        <div className="mx-auto flex h-9 max-w-[1280px] items-center justify-between px-4 text-xs md:px-6">
          <p className="flex items-center gap-2 text-primary-100">
            <i className="ri-information-line"></i>
            <span>本站赛事信息为演示数据，仅供界面展示参考</span>
          </p>
          <div className="flex items-center gap-4 text-primary-100">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>2026 年 10 月 12 日
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>今日 12 场赛事进行中
            </span>
          </div>
        </div>
      </div>

      <div
        className={`w-full border-b transition-all duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1280px] items-center justify-between gap-4 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-extrabold text-background-50">
              51
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-base font-extrabold text-foreground-950 md:text-lg">
                51每日大赛
              </span>
              <span className="text-[11px] tracking-wide text-foreground-500">
                今日黑聚集地详情
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 lg:flex"
            >
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索赛事名称"
                className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开菜单"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 md:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
            <form
              onSubmit={handleSearch}
              className="mb-4 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
            >
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索赛事名称"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50"
              >
                搜索
              </button>
            </form>
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center justify-between rounded-md px-4 py-3 text-sm font-medium ${
                    isActive(link.path)
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {link.name}
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}